import React from "react";
import {
  Gem,
  Truck,
  Headphones,
  Percent,
  Sparkles,
  Heart,
  ShieldCheck,
  Calendar,
  Gift,
} from "lucide-react";

const KEY_BENEFITS = [
  { icon: Percent, title: "Extra 20% Off", desc: "on medicines" },
  { icon: Truck, title: "Free Delivery", desc: "on all orders" },
  { icon: Headphones, title: "Priority Support", desc: "24×7 expert help" },
  { icon: Sparkles, title: "Early Access", desc: "to deals & offers" },
  { icon: Heart, title: "Health Benefits", desc: "exclusive partner benefits" },
  { icon: ShieldCheck, title: "No Minimum Order", desc: "value for free delivery" },
];

const EXCLUSIVE = [
  { icon: Heart, label: "Free Health Consultations", sub: "1 free consultation every month" },
  { icon: Percent, label: "Lab Test Discounts", sub: "Up to 25% off on lab tests" },
  { icon: Sparkles, label: "Wellness Rewards", sub: "Earn extra points on every order" },
  { icon: Calendar, label: "Birthday Bonus", sub: "Special surprise on your birthday" },
];

const SAVINGS = [
  { label: "Total Saved", value: "₹6,250" },
  { label: "Coupons Used", value: "18" },
  { label: "Free Deliveries", value: "24" },
  { label: "Delivery Saved", value: "₹2,340" },
];

export default function Membership() {
  const yearlySaved = 6250;
  const yearlyCap = 10000;
  const pct = Math.min(100, Math.round((yearlySaved / yearlyCap) * 100));

  return (
    <div data-testid="account-membership" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          My Membership
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your membership, explore benefits and unlock exclusive perks.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white flex items-center justify-center">
              <Gem className="h-5 w-5" />
            </div>
            <div>
              <p className="font-display text-xl font-bold text-gray-900">
                Dyntra Premium{" "}
                <span className="inline-flex items-center rounded-full bg-[#F3EFFC] text-[#4B2AAD] text-[10px] font-bold px-2 py-0.5 ml-1">
                  Premium Member
                </span>
              </p>
              <p className="text-xs text-gray-500">
                Experience the best of health &amp; wellness with exclusive benefits curated for you.
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#F5F3FB] p-3 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#4B2AAD]" />
              <div>
                <p className="text-[10px] uppercase font-semibold text-gray-500">
                  Member Since
                </p>
                <p className="text-sm font-bold text-gray-900">12 May 2023</p>
              </div>
            </div>
            <div className="rounded-xl bg-[#F5F3FB] p-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#4B2AAD]" />
              <div>
                <p className="text-[10px] uppercase font-semibold text-gray-500">
                  Membership ID
                </p>
                <p className="text-sm font-bold text-gray-900">DYNTR-PRM-4587</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-5 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-90">Membership Renews On</p>
            <p className="font-display text-2xl font-extrabold mt-1">12 May 2025</p>
            <p className="text-xs opacity-90 mt-1">45 days left</p>
          </div>
          <button className="mt-4 rounded-xl bg-white text-[#4B2AAD] text-sm font-bold py-2 hover:bg-white/90">
            Renew Membership
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-sm font-bold text-gray-900">
            Membership Usage
          </h3>
          <p className="text-xs text-gray-500">
            You&apos;re making the most of your Premium benefits.
          </p>
        </div>
        <p className="text-xs font-semibold text-gray-500">Yearly Savings Used</p>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-[#F3EFFC] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#4B2AAD] to-[#6B4CD9]"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-sm font-bold text-gray-900">
            ₹{yearlySaved.toLocaleString()} / ₹{yearlyCap.toLocaleString()}
          </span>
        </div>
        <p className="mt-2 text-xs text-gray-500">{pct}% used</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-sm font-bold text-gray-900 mb-3">
            Key Benefits
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {KEY_BENEFITS.map((b) => {
              const Ico = b.icon;
              return (
                <div
                  key={b.title}
                  className="rounded-2xl bg-[#F5F3FB] p-3 text-center"
                >
                  <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center mx-auto">
                    <Ico className="h-4 w-4 text-[#4B2AAD]" />
                  </div>
                  <p className="mt-2 text-xs font-bold text-gray-900">{b.title}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-display text-sm font-bold text-gray-900 mb-3">
            Exclusive Services
          </h3>
          <ul className="space-y-3">
            {EXCLUSIVE.map((e) => {
              const Ico = e.icon;
              return (
                <li key={e.label} className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#F3EFFC] flex items-center justify-center shrink-0">
                    <Ico className="h-4 w-4 text-[#4B2AAD]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{e.label}</p>
                    <p className="text-xs text-gray-500">{e.sub}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <h3 className="font-display text-sm font-bold text-gray-900">
          Your Order Savings
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          See how much you&apos;ve saved with Premium
        </p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SAVINGS.map((s) => (
            <div key={s.label} className="rounded-2xl bg-[#F5F3FB] p-4 text-center">
              <p className="text-lg font-bold text-[#4B2AAD]">{s.value}</p>
              <p className="text-[11px] text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] text-gray-400 text-center">
          Savings calculated for the current membership period.
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-[#4B2AAD] to-[#6B4CD9] text-white p-5 flex flex-col md:flex-row items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <Gift className="h-8 w-8" />
          <div>
            <p className="font-display text-lg font-bold">Keep enjoying premium benefits</p>
            <p className="text-xs opacity-90">
              Renew your membership and continue unlocking exclusive health &amp; savings benefits.
            </p>
          </div>
        </div>
        <button className="rounded-xl bg-white text-[#4B2AAD] font-bold px-5 py-2.5 hover:bg-white/90">
          Renew Now
        </button>
      </div>
    </div>
  );
}
